/**
 * Shared Zod helper builders for optional fields that may arrive as empty
 * strings from HTML form inputs or JSON payloads.
 *
 * Root cause: `z.string().optional()` accepts `undefined` but rejects `""`.
 * HTML form inputs always produce `""` when the user leaves them blank.
 * These helpers add a z.preprocess step that coerces `""` → `undefined`
 * before any further validation, so server schemas accept both missing and
 * blank values consistently.
 *
 * Usage:
 *   email:       optionalEmail()      // "" | undefined → undefined; non-empty → email-validated
 *   phone:       optionalString(20)   // "" | undefined → undefined; non-empty → max(20)
 *   note:        optionalString()     // "" | undefined → undefined; any non-empty string ok
 *   openingDate: optionalDate()       // "" | undefined → undefined; non-empty → YYYY-MM-DD
 *   dueDate:     optionalDatetime()   // "" | undefined → undefined; non-empty → ISO datetime
 *   gender:      optionalEnum(genderSchema)
 */

import { z } from 'zod';

/** Regex for YYYY-MM-DD date fields. */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Optional free-text string. Coerces `""` → `undefined`.
 * Accepts an optional maxLength constraint applied to non-empty values.
 */
export function optionalString(maxLength?: number) {
  const base = maxLength != null ? z.string().max(maxLength) : z.string();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preprocess input is untyped by design
  return z.preprocess((val: any) => (val === '' ? undefined : val), base.optional());
}

/**
 * Optional email field. Coerces `""` → `undefined`; validates email format
 * only when a non-empty value is present.
 */
export function optionalEmail() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preprocess input is untyped by design
  return z.preprocess((val: any) => (val === '' ? undefined : val), z.string().email().optional());
}

/**
 * Optional YYYY-MM-DD date string. Coerces `""` → `undefined`; validates
 * format only when a non-empty value is present.
 */
export function optionalDate() {
  return z.preprocess(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preprocess input is untyped by design
    (val: any) => (val === '' ? undefined : val),
    z.string().regex(DATE_PATTERN, 'Invalid date: must match YYYY-MM-DD').optional(),
  );
}

/**
 * Optional ISO datetime string (with timezone offset). Coerces `""` →
 * `undefined`; validates format only when a non-empty value is present.
 */
export function optionalDatetime() {
  return z.preprocess(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preprocess input is untyped by design
    (val: any) => (val === '' ? undefined : val),
    z.string().datetime({ offset: true }).optional(),
  );
}

/**
 * Optional enum field. Coerces `""` → `undefined`.
 * Pass the base enum schema (e.g. `z.enum([...])`) as argument.
 */
export function optionalEnum<T extends z.ZodTypeAny>(enumSchema: T) {
  return z.preprocess(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preprocess input is untyped by design
    (val: any) => (val === '' ? undefined : val),
    enumSchema.optional(),
  );
}
